import * as api from '@api';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@/hooks/useApi';
import useToast from '@/hooks/useToast';
import useNetworkCompaniesList from './useNetworkCompaniesList';
import useTranslator from './useTranslator';

export default function useComment({
  questionnaireFormQuestionId,
  requestId,
  onCommentAdded,
}) {
  const { toast } = useToast();
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState([]);
  const [sortByNewest, setSortByNewest] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const commentsBoxRef = useRef(null);
  const [emptyCommentError, setEmptyCommentError] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [repliedCommentId, setRepliedCommentId] = useState(null);
  const [commentToPerformAction, setCommentToPerformAction] = useState(null);
  const networkCompaniesList = useNetworkCompaniesList();
  const { getTranslation } = useTranslator({ page: 'comment-section' });

  const { data } = useQuery(
    ['getComments', questionnaireFormQuestionId],
    () =>
      api.supplierquestionnaire.questionnaireFormQuestionCommentsGet(
        requestId,
        questionnaireFormQuestionId,
      ),
    {
      onSuccess: ({ status, error }) => {
        if (status >= 400) {
          return toast.error(error.message);
        }
      },
      gcTime: 20,
      enabled: isExpanded,
    },
  );

  useEffect(() => {
    if (data) {
      setComments([
        ...data.data.data.map(d => ({
          ...d,
          content: d.questionComment,
          commentedAt: d.createdAt,
          commentedByUserId: d?.updatedByUserId,
          commentedByCompanyId: d?.updatedByCompanyId,
          commentedByUserName: d?.updatedByUserName,
          commentedByCompanyName: d?.updatedByCompanyName,
        })),
      ]);
      onCommentAdded([...data.data.data].length);
    }
  }, [data]);

  const getCommentImageUrl = commentedByCompanyId => {
    const company = networkCompaniesList.find(
      company => company.id === commentedByCompanyId,
    );
    return company?.featuredImageFileUrl;
  };
  const handleSubmitComment = () => {
    if (commentText.trim().length < 1) setEmptyCommentError(true);
    setSubmittingComment(true);
    return api.supplierquestionnaire
      .questionnaireFormQuestionCommentAdd({
        requestId,
        questionComment: commentText,
        questionId: questionnaireFormQuestionId,
      })
      .then(addCommentResponse => {
        if (addCommentResponse.status === 201) {
          setComments([
            ...comments,
            {
              ...addCommentResponse.data,
              content: addCommentResponse.data?.questionComment,
              commentedAt: addCommentResponse.data?.createdAt,
              commentedByUserId: addCommentResponse.data?.updatedByUserId,
              commentedByCompanyId: addCommentResponse.data?.updatedByCompanyId,
              commentedByUserName: addCommentResponse.data?.updatedByUserName,
              commentedByCompanyName:
                addCommentResponse.data?.updatedByCompanyName,
              repliedCommentId,
            },
          ]);
          setCommentText('');
          setRepliedCommentId(null);
          onCommentAdded(
            [...comments, { ...addCommentResponse.data, repliedCommentId }]
              .length,
          );
          toast.success(getTranslation('comment.action.respond.success'), {
            position: 'bottom-center',
          });
        } else {
          toast.error(addCommentResponse.error, {
            position: 'bottom-center',
          });
        }
      })
      .catch(error => {
        toast.error(error, {
          position: 'bottom-center',
        });
      })
      .finally(() => {
        setSubmittingComment(false);
      });
  };

  const handleTextareaKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmitComment();
    }
  };

  const handleEditComment = () => {
    const commentFound = commentToPerformAction;
    delete commentFound.editAction;
    const foundCommentIndex = comments.findIndex(
      comment => comment.id === commentFound.id,
    );
    if (foundCommentIndex !== -1) {
      const allComments = [...comments];
      allComments[foundCommentIndex] = { ...commentFound, edited: true };
      return api.supplierquestionnaire
        .updateComment(comments[foundCommentIndex].id, {
          questionComment: commentFound.content,
        })
        .then(response => {
          if (response.status === 201) {
            setComments([...allComments]);
            setCommentToPerformAction(null);
          } else {
            toast.error(response.error, {
              position: 'bottom-center',
            });
          }
        })
        .catch(error => {
          toast.error(error, {
            position: 'bottom-center',
          });
        })
        .finally(() => {
          setSubmittingComment(false);
        });
    }
  };

  const handleDeleteComment = () => {
    const commentFound = commentToPerformAction;
    const foundCommentIndex = comments.findIndex(
      comment => comment.id === commentFound.id,
    );
    if (foundCommentIndex !== -1) {
      return api.supplierquestionnaire
        .deleteComment(commentFound.id)
        .then(deleteCommentResponse => {
          if (deleteCommentResponse.status === 204) {
            comments.splice(foundCommentIndex, 1);
            setComments(comments);
            setRepliedCommentId(null);
            setCommentToPerformAction(null);
            toast.success(getTranslation('comment.action.respond.deleted'), {
              position: 'bottom-center',
            });
          } else {
            toast.error(deleteCommentResponse.error, {
              position: 'bottom-center',
            });
          }
        })
        .catch(error => {
          toast.error(error, {
            position: 'bottom-center',
          });
        })
        .finally(() => {
          setSubmittingComment(false);
        });
    }
  };

  return {
    commentText,
    comments,
    sortByNewest,
    setCommentText,
    setComments,
    commentsBoxRef,
    setSortByNewest,
    isExpanded,
    setIsExpanded,
    handleSubmitComment,
    emptyCommentError,
    setEmptyCommentError,
    submittingComment,
    setSubmittingComment,
    handleTextareaKeyDown,
    repliedCommentId,
    setRepliedCommentId,
    getCommentImageUrl,
    commentToPerformAction,
    setCommentToPerformAction,
    handleEditComment,
    handleDeleteComment,
  };
}
